import React, { FC, ReactNode, useState } from 'react'
import { Flex, Input, Typography } from 'antd'
import { observer } from 'mobx-react'
import { DeliveryChannel } from '@pushly/models/lib/enums'
import type { GeneratedNotificationContent } from '@pushly/models/lib/structs/pushly-ai/generated-notification-content'
import { describeAdvisory, isPreviewUrl, useBrandVoice } from '../../../modules/ai/brand-voice'
import type { BrandVoicePreviewOutcome } from '../../../modules/ai/brand-voice'
import { SendPreviewWell } from '../send-preview-well'
import type { ISendPreviewOutcome, SendPreviewState } from '../send-preview-well'
import BrandVoiceFilled from '../../icons/brand-voice-filled'
import { NotificationVariantModel } from '../../../models/notification/notification-variant.model'
import { DomainDto } from '../../../dtos/domain'

type Variant = GeneratedNotificationContent

interface IBrandVoiceSendPreview {
    domain: DomainDto
    /** Whether the domain has a Brand Voice to write with. Without one there is nothing to preview. */
    hasDirective: boolean
    /** True until the Brand Voice has loaded, when `hasDirective` is not yet an answer. */
    directiveLoading?: boolean
    /** Generating is a step of creating a notification, and the server asks for the same permission. */
    canGenerate: boolean
}

/**
 * The notification a variant would be, as the notification builder's own preview takes it, with the domain's default
 * icon and badge. Built when it is drawn, never when this module loads: the model sits in an import cycle.
 */
const previewVariant = (variant: Variant, image: string | undefined, landingUrl: string) =>
    NotificationVariantModel.build({
        channels: [DeliveryChannel.WEB],
        content: {
            default: {
                title: variant.title ?? '',
                body: variant.body ?? '',
                imageUrl: image,
                landingUrl,
                isUsingDomainDefaultIcon: true,
                isUsingDomainDefaultBadge: true,
            },
        },
    })

const pageErrorNotice = (pageError: NonNullable<BrandVoicePreviewOutcome['pageError']>) => ({
    title: 'The page could not be read',
    description: (
        <>
            {pageError.message}
            {pageError.referenceUrl && (
                <>
                    {' '}
                    <a href={pageError.referenceUrl} target="_blank" rel="noopener noreferrer">
                        Learn more
                    </a>
                </>
            )}
        </>
    ),
})

/** The advisory under the drawn variant: why it could not be confirmed, and each rule it missed. */
const advisoryNote = (outcome: BrandVoicePreviewOutcome): ReactNode => {
    if (!outcome.policyError) {
        return undefined
    }

    const advisory = describeAdvisory(outcome.policyError, 0)

    return (
        <Flex vertical gap={4}>
            <span>{advisory.message}</span>
            {advisory.rules.map((rule, i) => (
                <span key={i}>
                    {rule.tier === 'guaranteed' ? 'Enforced' : 'Guidance'}: {rule.rule}. {rule.reason}
                </span>
            ))}
        </Flex>
    )
}

/** Why the variant was written, in a panel of its own so it reads as what came back rather than another input. */
const generationDetails = (reason: string | undefined): ReactNode =>
    reason ? (
        <section className="ai-preview-details" aria-labelledby="brand-voice-generation-details">
            <Flex align="center" gap={8} className="ai-preview-details-header">
                <BrandVoiceFilled />
                <span id="brand-voice-generation-details">Generation Details</span>
            </Flex>
            <Typography.Paragraph className="ai-preview-details-body">{reason}</Typography.Paragraph>
        </section>
    ) : undefined

/** What the owner holds, as the shared well draws it: the first variant, with its reason as the caption. */
export const toSendPreviewOutcome = (outcome: BrandVoicePreviewOutcome): ISendPreviewOutcome<Variant> => {
    if (outcome.pageError) {
        return { result: null, notice: pageErrorNotice(outcome.pageError) }
    }

    const first = outcome.variants[0]

    return first
        ? { result: first, caption: generationDetails(first.reason), advisory: advisoryNote(outcome) }
        : { result: null }
}

/**
 * Asks Brand Voice to write the notification for a page, through the shared send preview, which draws it the way the
 * notification builder does. This adds what is Brand Voice's own: the page to write for, and the words.
 */
export const BrandVoiceSendPreviewWell: FC<IBrandVoiceSendPreview> = observer(
    ({ domain, hasDirective, directiveLoading = false, canGenerate }) => {
        const brandVoice = useBrandVoice()
        const [url, setUrl] = useState('')
        const validUrl = isPreviewUrl(url)

        // The owner holds the preview as it is; the shared well takes it as the feature's result.
        const preview: SendPreviewState<Variant> =
            brandVoice.preview.status === 'ready' || brandVoice.preview.status === 'refreshFailed'
                ? { ...brandVoice.preview, data: toSendPreviewOutcome(brandVoice.preview.data) }
                : brandVoice.preview

        // Nothing is said about a missing Brand Voice until it is known to be missing.
        const blockedBy = directiveLoading
            ? undefined
            : !hasDirective
            ? 'Set up your Brand Voice above before previewing a send.'
            : !canGenerate
            ? 'Previewing writes notification copy, which needs permission to create notifications.'
            : undefined

        return (
            <SendPreviewWell<Variant>
                domain={domain}
                segments={brandVoice.segments}
                onLoadSegments={brandVoice.loadSegments}
                preview={preview}
                onRun={(segmentId) => brandVoice.intents['brandVoice.preview'].perform({ segmentId, url })}
                onClear={() => void brandVoice.intents['brandVoice.resetPreview'].perform()}
                title="Preview a generated send"
                infoAbout="previewing a generated send"
                info="Brand Voice writes the notification it would send for a page, for the segment you choose. Nothing is sent and nothing is saved."
                idle={{
                    icon: <BrandVoiceFilled />,
                    title: 'Nothing previewed yet',
                    description:
                        'Enter a page and select Preview to see the notification Brand Voice would write for it.',
                }}
                empty={{
                    icon: <BrandVoiceFilled />,
                    title: 'Nothing to send yet',
                    description: 'Brand Voice wrote nothing for this page. Try another page.',
                }}
                failed={{
                    title: 'The preview could not be run',
                    description: 'Nothing was sent and nothing changed, so this is safe to try again.',
                }}
                canRun={validUrl && !blockedBy && !directiveLoading}
                resetKey={url}
                inputs={
                    <Flex vertical gap={8}>
                        <Typography.Text id="brand-voice-preview-url" className="ai-muted-text">
                            Page URL
                        </Typography.Text>
                        <Input
                            aria-labelledby="brand-voice-preview-url"
                            placeholder="https://your-domain.com/an-article"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            status={url.trim() && !validUrl ? 'error' : undefined}
                        />
                        {url.trim() && !validUrl && (
                            <Typography.Text type="danger">
                                Enter a full web address, starting with https://
                            </Typography.Text>
                        )}
                        {blockedBy && <Typography.Text className="ai-muted-text">{blockedBy}</Typography.Text>}
                    </Flex>
                }
                toSource={(variant, image) => previewVariant(variant, image, url.trim())}
                imageOf={(variant) => variant.image}
            />
        )
    },
)
